import { useActor, useMachine } from "@xstate/react";
import React, { FunctionComponent, useEffect } from "react";
import { State } from "xstate";
import { Node } from "@iventis/domain-model/model/node";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { inlineTextIconMargin } from "@iventis/styles";
import { Content } from "@iventis/translations/content/typed-content";
import { isAdmin, xstateDevTools } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FormWizardTemplate, FormWizardTitle } from "@iventis/components";
import { PermissionsEditComponent, PermissionsEditHeader } from "./permissions-edit";
import { permissionsEditMachine } from "./permissions-edit.machine";
import { hasPermissionsChanged } from "./permission-utilitites";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PermissionsEditActorComponent: FunctionComponent<{ parentState: State<any, any, any, any, any> }> = ({ parentState }) => {
    const [state, send] = useActor(parentState.children.permissions);
    return <PermissionsEditComponent state={state} send={send} />;
};

export const PermissionsEditForm: React.FC<{
    node: Node;
    close: () => void;
}> = ({ node, close }) => {
    const [state, send] = useMachine(permissionsEditMachine, {
        devTools: xstateDevTools,
        context: {
            node,
        },
    });
    const { originalPermissions, latestPermissions } = state.context;
    const saving = state.matches("saving");

    const translate = useIventisTranslate();

    useEffect(() => {
        if (state.matches("finished")) close();
    }, [state.value]);
    return (
        <FormWizardTemplate
            title={<FormWizardTitle title={<PermissionsEditHeader node={node} />} />}
            dividers={false}
            currentStage={0}
            stages={[
                {
                    primaryButtonCallback: () => send([{ type: "SAVE" }, { type: "CLOSE" }]),
                    primaryButtonText: (
                        <>
                            {saving ? (
                                <>
                                    <FontAwesomeIcon icon="circle-notch" spin style={{ marginRight: inlineTextIconMargin }} /> {translate(Content.common.saving)}
                                </>
                            ) : (
                                translate(Content.common.buttons.saveAndFinish)
                            )}
                        </>
                    ),
                    isValid: hasPermissionsChanged(originalPermissions, latestPermissions) && isAdmin(node.authorisation),
                    secondaryButtons: [
                        {
                            buttonText: translate(Content.common.buttons.cancel),
                            onButtonPressed: () => send({ type: "CLOSE" }),
                            buttonDisabled: saving,
                            dataCy: "permissions-cancel-button",
                        },
                    ],
                },
            ]}
        >
            <PermissionsEditComponent state={state} send={send} close={close} includeHeader={false} />
        </FormWizardTemplate>
    );
};
